import * as React from 'react';
import { connect } from 'react-redux';

import { Form, FormField } from 'components/form';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Input from 'components/inputs/input/Input';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { ModalFrame, ModalFrameFooter, ModalFrameInner, ModalFrameSeparator } from 'components/modal';

import { danger } from 'redux-store/reducers/common-reducer';
import { putOfferState } from 'modules/subcampaigns/reducers/offer';
import { fetchSubcampaign } from 'modules/subcampaigns/reducers/subcampaign';

import { t } from 'utils/language';
import { isRequired } from 'utils/validators';
import { isNegotiationsReopened } from 'utils/subcampaign';

import './subcampaign-offer-decline-modal.scss';

const OFFER_DECLINE_OPTIONS = ['low_offer', 'no_time', 'style_fit', 'brand_fit', 'other'];

import type {
  Data,
  MixedSelectDropdownItem,
  Subcampaign,
  SubcampaignOfferState
} from 'types';

type OwnProps = {
  subcampaign: Subcampaign,
  onCloseModal: () => void
};

type DispatchProps = {
  dispatchPutOfferState: (b: number, a: SubcampaignOfferState) => Promise<void>,
  dispatchDanger: () => Promise<void>,
  dispatchFetchSubcampaign: (a: number) => Promise<void>
};

type Props = OwnProps & DispatchProps;

type State = {
  reason: string,
  comment: string,
  isModified: boolean
};

class SubcampaignOfferDeclineModal extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      reason: '',
      comment: '',
      isModified: false
    };
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { reason } = this.state;

    return OFFER_DECLINE_OPTIONS.map(value => ({
      identifier: value,
      isDisabled: false,
      isSelected: reason === value,
      label: t(`subcampaign_offer_decline.options[${value}]`),
      value
    }));
  };

  handleSubmit = (): void => {
    const { onCloseModal, subcampaign, dispatchPutOfferState, dispatchFetchSubcampaign } = this.props;
    const { comment, reason } = this.state;

    const offerId = subcampaign.updatedOffer && subcampaign.updatedOffer.id || subcampaign.offer.id;
    const offerState: SubcampaignOfferState = {
      state: 'declined',
      reason: this.getReason(reason),
      comment: undefined
    };

    if (comment) {
      offerState.comment = comment;
    }

    dispatchPutOfferState(offerId, offerState).then(() => dispatchFetchSubcampaign(subcampaign.id));

    onCloseModal();
  };

  handleValidationErrors = (): void => {
    this.props.dispatchDanger();
  };

  handleReasonChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    this.setState({
      reason: selectedItem?.identifier || updatedItems[0].identifier,
      isModified: true
    });
  };

  handleCommentChange = (event: Event): void => {
    const input = event.target;
    const value = (input instanceof HTMLTextAreaElement && input.value) || ''; // eslint-disable-line no-undef

    this.setState({
      comment: String(value),
      isModified: true
    });
  };

  getReason = (value: string): string | undefined | null => {
    // todo check system message in this case: empty reason ?
    if (!value) {
      return undefined;
    }

    if (value === 'other') {
      return t('subcampaign_offer_decline.options.other_reason');
    }

    return t(`subcampaign_offer_decline.options[${value}]`);
  };

  renderModalForRenegotiation() {
    const { comment } = this.state;

    return (
      <FormField
        label={t('subcampaign_offer_decline.reopened.additional_message')}
        direction="column"
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
      >
        <Input
          value={comment}
          placeholder={t('subcampaign_offer_decline.reopened.additional_message_placeholder')}
          onChange={this.handleCommentChange}
          multiline
          width="responsive"
        />
      </FormField>
    );
  }

  renderModal() {
    const { reason, comment } = this.state;
    const dropdownItems = this.getDropdownItems();

    return (
      <React.Fragment>
        <FormField
          label={t('subcampaign_offer_decline.pending.reason')}
          showRequiredMark
          validators={[isRequired(reason)]}
          direction="column"
          labelType="highlighted"
          fontSize="small"
          tooltipMessage={t('subcampaign_offer_decline.pending.tooltip')}
        >
          <MixedSelectDropdown
            key={`subcampaign-offer-decline__dropdown--${reason}`}
            name="subcampaign-offer-decline__dropdown"
            items={dropdownItems}
            placeholder={t('fields_general.fields.influencer_gender_placeholder')}
            withSelectedInList
            className="subcampaign-offer-decline__dropdown"
            onItemsToggle={this.handleReasonChange}
          />
        </FormField>
        <FormField
          label={t('subcampaign_offer_decline.pending.additional_message')}
          direction="column"
          labelType="highlighted"
          fontSize="small"
          showValidationIcon={false}
        >
          <Input
            value={comment}
            placeholder={t('subcampaign_offer_decline.pending.additional_message_placeholder')}
            onChange={this.handleCommentChange}
            multiline
            width="responsive"
          />
        </FormField>
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { subcampaign, onCloseModal } = this.props;
    const { isModified } = this.state;
    const isRenegotiationReopened = isNegotiationsReopened(subcampaign);

    return (
      <ModalFrame
        title={t(`subcampaign_offer_decline.${isRenegotiationReopened ? 'reopened' : 'pending'}.title`)}
        subtitle={t(`subcampaign_offer_decline.${isRenegotiationReopened ? 'reopened' : 'pending'}.subtitle`)}
      >
        <ModalFrameInner
          narrowContent
        >
          {isRenegotiationReopened && <ModalFrameSeparator /> }
          <Form
            onSubmit={this.handleSubmit}
            onValidationErrors={this.handleValidationErrors}
          >
            {
              isModified &&
                <PromptExit type="modal" />
            }
            {
              isRenegotiationReopened
                ? this.renderModalForRenegotiation()
                : this.renderModal()
            }
            <ModalFrameFooter>
              <ActionButton
                color="tertiary"
                onClick={onCloseModal}
              >
                {t('action_button_cancel')}
              </ActionButton>
              <ActionButton
                type="submit"
              >
                {t('subcampaign_button_decline')}
              </ActionButton>
            </ModalFrameFooter>
          </Form>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchPutOfferState: (offerId: number, offerState: SubcampaignOfferState) => dispatch(putOfferState(offerId, offerState)),
  dispatchDanger: (): Promise<void> => dispatch(danger(t('missing_fields'))),
  dispatchFetchSubcampaign: (subcampaignId: number): Promise<void> => dispatch(fetchSubcampaign(subcampaignId))
});

export default connect(null, mapDispatchToProps)(SubcampaignOfferDeclineModal);
